// The dock: the Incoming Inspection Report (IIR), on a tablet, under the
// receiver's own login (Amit, 22 Oct 2026; migration 017).
//
// TWO INSPECTIONS BY TWO PEOPLE, handed from one to the other (Amit, 27 Oct 2026;
// migration 033, contract "the receiving handoff"):
//   1. VEHICLE - the warehouse (RECEIVE), at the truck before unloading: which
//      supplier, which PO lines are on it (received in D365 or not), carrier, rego,
//      date, the seven vehicle checks. "Hand over to QA".
//   2. GOODS - QA (RELEASE), after the vehicle passed: picked from "Goods to
//      inspect" (or ?vehicle=<id> from the Teams card), the vehicle result shown
//      read-only, then six product checks per batch row.
// A reason on every FAIL. The CofA match is NOT asked: the app computes it from the
// lot and shows it in the office (it is not this person's to tick).
//
// Every submit goes through the IIR queue (offline/iirQueue.ts, B2): stamped
// with this account at capture, held on the tablet if the Wi-Fi is down, sent
// when it is back. A refused inspection stays on the tablet with the server's
// sentence; it is never silently dropped.
//
// It gates nothing. A QA person sees it beside the certificate in Goods in.
//
// PHOTOS (migration 019) are ALWAYS OPTIONAL (Amit, 22 Oct 2026): of the delivery,
// of a row, or of a failed check. They wait in the queue with the inspection and
// follow it once the server has it; a photo that does not go never holds the
// inspection back, and is shown here as "photo not sent" with Send again.
//
// A HALF-DONE INSPECTION IS KEPT ON THE TABLET (UI/UX audit, 31 Oct 2026, UX-08). The draft used
// to live only in React state, so an area tab, a reload, a discarded tab or a sign-in redirect
// dropped it without a word. It is now kept in the IIR database as it is typed (useIirDraft,
// photos included), stamped with the account that opened it, and offered back to that account -
// and only that account (rule 2's shape) - as "Resume the inspection of <supplier>". It is cleared
// when the inspection goes to the queue or its owner cancels, and a reload asks first while one is
// open. BrowserRouter has no useBlocker, so area tabs are not intercepted: keeping the draft is
// what makes leaving safe, not a prompt.
//
// NARROW SCREENS (end-to-end test, 22 Oct 2026): the dock is a phone as often as a
// tablet. Below `sm` a check's words sit ABOVE its Pass / Fail buttons, never
// squeezed beside them; the fields and buttons go full width.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { currentUpn } from '../../config/auth';
import { actionWithCode } from '../../lib/vocabulary';
import { useIirDraft, useIirQueue } from '../../offline/useIirQueue';
import type { IirContext } from '../../offline/iirQueue';
import { useMyGrants, useResource } from '../../shells/OfficeShell';
import { opFocus } from '../../shells/OperatorShell';
import { usePageTitle } from '../../lib/pageTitle';
import { PhotoPicker } from './PhotoPicker';
import { ConfirmPanel, type ConfirmRequest } from './ConfirmPanel';
import type { RegisterPayload, Tone } from '../office/goodsInModel';
import {
  BATCH_CHECKS,
  VEHICLE_CHECKS,
  blankRow,
  answeredCount,
  brisbaneToday,
  checkId,
  deliveryGroups,
  dockDay,
  dockTime,
  draftSupplier,
  failedCount,
  fieldId,
  gaps,
  leaveOutLine,
  lineInspection,
  lineAsRegister,
  linkedDelivery,
  linkedReceiptIds,
  linkedVehicleId,
  newGoodsDraft,
  newGoodsDraftApart,
  newVehicleDraft,
  oldestFirst,
  openLinesQuery,
  photosToSend,
  pickerLines,
  poLine,
  refusalHint,
  restoreLine,
  resumableDraft,
  toPayload,
  vehicleOutcome,
  waitedFor,
  type Answer,
  type AwaitingVehicle,
  type DeliveryGroup,
  type Draft,
  type DraftPhoto,
  type DraftRow,
  type PhotoCheckCode,
} from './receiveModel';

const BTN =
  `min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) ` +
  `text-(length:--fs-md) font-semibold disabled:opacity-50 ${opFocus}`;
const PRIMARY =
  `min-h-[56px] rounded-(--radius) bg-(--primary) px-(--s5) text-(length:--fs-md) font-semibold ` +
  `text-(--primary-fg) disabled:opacity-50 ${opFocus}`;
/** The submit: PRIMARY, dimmed while it would refuse (aria-disabled keeps it pressable, UX-44). */
const SUBMIT = `${PRIMARY} aria-disabled:opacity-50`;
const INPUT =
  `min-h-[56px] w-full rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s3) ` +
  `text-(length:--fs-md) ${opFocus}`;
const CARD = 'rounded-(--radius) border border-(--border) bg-(--card) p-(--s4)';

/** A dock step's heading: its number, its name in QA's words, and whose step it is. */
function StepHeading({ step, title, who }: { step: 1 | 2; title: string; who: string }) {
  return (
    <div className="flex items-start gap-(--s3)">
      <span
        aria-hidden="true"
        className="inline-flex size-[36px] shrink-0 items-center justify-center rounded-full bg-(--primary) text-(length:--fs-md) font-bold text-(--primary-fg)"
      >
        {step}
      </span>
      <div className="min-w-0">
        <h2 className="m-0 text-(length:--fs-lg) font-semibold">
          <span className="sr-only">Step {step}: </span>
          {title}
        </h2>
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">{who}</p>
      </div>
    </div>
  );
}

/** GET /dock-waiting: received in D365, no dock inspection yet (29 Oct 2026). */
export interface DockWaitingPayload {
  since: string;
  deliveries: Array<{ receiptId: number; po: string; line: number; item: string; itemName: string | null; supplier: string | null; batch: string; packingSlip: string | null; received: string; qty: number | null; stockUnit: string | null }>;
  linesNotRead: number;
}

/**
 * QA's read-only list of what waits on the warehouse's vehicle check. Error is never "nothing waiting" (rule 15).
 *
 * READ-ONLY, AND IT LOOKS IT (UI/UX audit, 31 Oct 2026, UX-53). Its items used to wear CARD, the
 * same bordered box as "Goods to inspect" and the supplier buttons beside it, so QA tapped them and
 * nothing happened. They are now plain rows under a rule - nothing about them says "press" - with
 * the count in the lead line, oldest first (the delivery that has waited longest is the one to
 * chase), and how long each has waited beside its date.
 */
export function DockWaitingList({
  dock,
  today = brisbaneToday(),
}: {
  dock: { data: DockWaitingPayload | null; error: string | null; reload: () => void };
  today?: string;
}) {
  const deliveries = dock.data ? oldestFirst(dock.data.deliveries) : [];
  return (
    <section className="flex flex-col gap-(--s3)" aria-label="Waiting for the dock">
      <h2 className="m-0 text-(length:--fs-lg) font-semibold">Waiting for the dock</h2>
      <p className="m-0 text-(length:--fs-sm) text-(--muted)">
        {dock.data && deliveries.length > 0
          ? `${deliveries.length} ${deliveries.length === 1 ? 'delivery' : 'deliveries'} received in D365 with no vehicle check yet, oldest first. `
          : 'Received in D365 with no vehicle check yet. '}
        The goods inspection opens once the warehouse passes the vehicle.
      </p>
      {dock.error ? (
        <div role="alert" className={CARD}>
          <p className="m-0 font-semibold text-(--warn)">Could not read the deliveries waiting for the dock: {dock.error}. This is not the same as "nothing waiting".</p>
          <button type="button" className={`${BTN} mt-(--s2)`} onClick={dock.reload}>
            Try again
          </button>
        </div>
      ) : !dock.data ? (
        <p role="status" className="m-0 text-(--muted)">Loading the deliveries waiting for the dock…</p>
      ) : (
        <>
          {deliveries.length === 0 ? (
            <p className="m-0 text-(--muted)">Nothing received in the last two days is waiting for a vehicle check.</p>
          ) : (
            <ul className="m-0 flex list-none flex-col p-0">
              {deliveries.map((d) => {
                const waited = waitedFor(d.received, today);
                return (
                  <li key={`${d.receiptId}|${d.packingSlip ?? ''}|${d.batch}`} className="border-b border-(--border) py-(--s2) last:border-b-0">
                    <span className="block font-semibold wrap-anywhere">
                      {d.supplier ?? 'Unknown supplier'}: {d.itemName ?? d.item} ({poLine(d.po, d.line)})
                    </span>
                    <span className="block text-(length:--fs-sm) text-(--muted) wrap-anywhere">
                      Batch {d.batch}
                      {d.qty !== null ? ` · ${d.qty.toLocaleString('en-AU')} ${d.stockUnit ?? '(D365 stock unit)'}` : ''} · received {dockDay(d.received)}
                      {waited ? ` (${waited})` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {dock.data.linesNotRead > 0 ? (
            <p className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
              {dock.data.linesNotRead} received line{dock.data.linesNotRead === 1 ? '' : 's'} could not be read from D365, so may hold more deliveries.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
const LABEL = 'text-(length:--fs-sm) font-semibold text-(--muted)';

/** A register tone as text colour, on the existing tokens (UX-43). */
const TONE_TEXT: Record<Tone, string> = { bad: 'text-(--bad)', warn: 'text-(--warn)', ok: 'text-(--ok)', quiet: 'text-(--muted)' };

/** A Pass or Fail button without its colours: answerClass gives those, all of them. */
const ANSWER =
  `min-h-[56px] rounded-(--radius) border px-(--s4) text-(length:--fs-md) font-semibold ` +
  `disabled:opacity-50 ${opFocus}`;

/**
 * The colours of a Pass or Fail button. Selected is FILLED - the ok or bad colour as the
 * ground, the text on it in the primary foreground (white in light, dark in dark) - and
 * carries a mark, so it reads in bright light and without colour. Unselected stays outline.
 *
 * ⚠ EXACTLY ONE background and one border class, never the outline's plus an override:
 * two arbitrary-value classes of one property are decided by their order in the generated
 * stylesheet, not in className, and bg-(--card) sorts after bg-(--bad) - the
 * first build filled PASS and left FAIL looking unselected.
 */
export function answerClass(selected: boolean, which: 'PASS' | 'FAIL'): string {
  if (!selected) return 'border-(--border-strong) bg-(--card)';
  return which === 'PASS'
    ? 'border-(--ok) bg-(--ok) text-(--primary-fg)'
    : 'border-(--bad) bg-(--bad) text-(--primary-fg)';
}

/** What a photo is of, and the form's photos, for the pickers below. */
interface PhotoSlot {
  of: (rowKey: string | null, checkCode: PhotoCheckCode | null) => DraftPhoto[];
  total: number;
  add: (rowKey: string | null, checkCode: PhotoCheckCode | null) => (ps: Array<Pick<DraftPhoto, 'key' | 'blob' | 'capturedAtDevice'>>) => void;
  remove: (key: string) => void;
}

/** One check: PASS / FAIL, and the reason box (and an optional photo) once FAIL is pressed. */
export function CheckToggle({
  label,
  value,
  onChange,
  photo,
  id,
  flagged = false,
}: {
  label: string;
  value: Answer;
  onChange: (a: Answer) => void;
  /** Where a photo of this check goes, when it fails. Optional, always. */
  photo?: { slot: PhotoSlot; rowKey: string | null; code: PhotoCheckCode; where: string };
  /** The check's place on the form (checkId), so a gap can jump to it (UX-44). */
  id?: string;
  /** After a submit attempt, this check is one of the gaps: it says so where it is (UX-44). */
  flagged?: boolean;
}) {
  const pick = (result: 'PASS' | 'FAIL') => onChange({ ...value, result });
  return (
    <div id={id} tabIndex={id ? -1 : undefined} className="flex flex-col gap-(--s2) border-b border-(--border) py-(--s3) last:border-b-0">
      {/* The marker on a gap (UI/UX audit, 31 Oct 2026, UX-44): shown only after the person tried to
          send, beside the check it is about - never a list on a fresh form. Words, not only colour. */}
      {flagged ? (
        <p className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {value.result === null ? 'Not answered yet: press Pass or Fail.' : 'FAIL needs the reason below.'}
        </p>
      ) : null}
      <div className="flex flex-col gap-(--s2) sm:flex-row sm:items-center sm:gap-[var(--s3)]">
        <span className="min-w-0 text-(length:--fs-md) sm:flex-1">{label}</span>
        <div role="group" aria-label={label} className="grid grid-cols-2 gap-(--s2) sm:flex">
          <button
            type="button"
            aria-pressed={value.result === 'PASS'}
            onClick={() => pick('PASS')}
            className={`${ANSWER} sm:min-w-[96px] ${answerClass(value.result === 'PASS', 'PASS')}`}
          >
            {value.result === 'PASS' ? '✓ Pass' : 'Pass'}
          </button>
          <button
            type="button"
            aria-pressed={value.result === 'FAIL'}
            onClick={() => pick('FAIL')}
            className={`${ANSWER} sm:min-w-[96px] ${answerClass(value.result === 'FAIL', 'FAIL')}`}
          >
            {value.result === 'FAIL' ? '✕ Fail' : 'Fail'}
          </button>
        </div>
      </div>
      {value.result === 'FAIL' ? (
        <>
          <label className="flex flex-col gap-(--s1)">
            <span className={LABEL}>What was wrong (required)</span>
            <input className={INPUT} value={value.reason} onChange={(e) => onChange({ ...value, reason: e.target.value })} />
          </label>
          {photo ? (
            <div className="flex flex-col gap-(--s1)">
              <span className={LABEL}>Photo of it (optional)</span>
              <PhotoPicker
                label={`${photo.where}: ${label}`}
                photos={photo.slot.of(photo.rowKey, photo.code)}
                total={photo.slot.total}
                onAdd={photo.slot.add(photo.rowKey, photo.code)}
                onRemove={photo.slot.remove}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex w-full min-w-0 flex-col gap-(--s1) sm:w-auto sm:min-w-[12rem] sm:flex-1">
      <span className={LABEL}>{label}</span>
      {children}
    </label>
  );
}

function BatchCard({
  row,
  index,
  onChange,
  onRemove,
  removeLabel = 'Remove row',
  photos,
  flagged,
}: {
  row: DraftRow;
  index: number;
  onChange: (r: DraftRow) => void;
  onRemove?: () => void;
  /** "Not on the truck" when the row is its PO line's last: removing it leaves the line out. */
  removeLabel?: string;
  photos: PhotoSlot;
  /** The ids of the gaps a submit attempt found (UX-44); empty before one. */
  flagged: ReadonlySet<string>;
}) {
  const where = `row ${index + 1}`;
  return (
    <section className={CARD} aria-label={`Row ${index + 1}`}>
      <div className="flex flex-wrap items-baseline gap-x-(--s3) gap-y-(--s1)">
        <h3 className="m-0 min-w-0 text-(length:--fs-lg) font-semibold wrap-anywhere">
          {index + 1}. {row.itemName}
        </h3>
        <span className="min-w-0 font-mono text-(length:--fs-sm) text-(--muted) wrap-anywhere">
          {poLine(row.po, row.line)}
          {row.batchId ? ` · D365 batch ${row.batchId}` : ''}
        </span>
        {onRemove ? (
          <button type="button" className={`${BTN} w-full sm:ml-auto sm:w-auto`} onClick={onRemove}>
            {removeLabel}
          </button>
        ) : null}
      </div>
      <div className="mt-(--s3) flex flex-wrap gap-(--s3)">
        <Field label="Lot number, as printed on the bag">
          <input className={`${INPUT} font-mono`} value={row.lotAsPrinted} onChange={(e) => onChange({ ...row, lotAsPrinted: e.target.value })} />
          {row.batchId ? (
            <span className="text-(length:--fs-sm) text-(--muted)">
              D365 received batch <span className="font-mono">{row.batchId}</span>. Type what the bag says, not this.
            </span>
          ) : null}
        </Field>
        <Field label="Date of manufacture">
          <input id={fieldId('manufacturedDate', row.key)} type="date" className={INPUT} value={row.manufacturedDate} onChange={(e) => onChange({ ...row, manufacturedDate: e.target.value })} />
        </Field>
        <Field label="Best before">
          <input id={fieldId('bestBeforeDate', row.key)} type="date" className={INPUT} value={row.bestBeforeDate} onChange={(e) => onChange({ ...row, bestBeforeDate: e.target.value })} />
        </Field>
        <Field label="Quantity received (kg)">
          <input id={fieldId('quantityKg', row.key)} inputMode="decimal" className={`${INPUT} font-mono`} value={row.quantityKg} onChange={(e) => onChange({ ...row, quantityKg: e.target.value })} />
        </Field>
      </div>
      <div className="mt-(--s2)">
        {BATCH_CHECKS.map((c) => (
          <CheckToggle
            key={c.key}
            label={c.label}
            value={row.checks[c.key]}
            onChange={(a) => onChange({ ...row, checks: { ...row.checks, [c.key]: a } })}
            photo={{ slot: photos, rowKey: row.key, code: c.key, where }}
            id={checkId(row.key, c.key)}
            flagged={flagged.has(checkId(row.key, c.key))}
          />
        ))}
      </div>
      <Field label="Non-conformance details / comments">
        <input className={INPUT} value={row.comment} onChange={(e) => onChange({ ...row, comment: e.target.value })} />
      </Field>
      <div className="mt-(--s3) flex flex-col gap-(--s1)">
        <span className={LABEL}>Photos of this row (optional)</span>
        <PhotoPicker label={where} photos={photos.of(row.key, null)} total={photos.total} onAdd={photos.add(row.key, null)} onRemove={photos.remove} />
      </div>
    </section>
  );
}

/**
 * A photo the server refused (a 4xx; a 5xx or a dropped network just waits). On a corrected
 * inspection there is nothing to retry: the photo belongs on the record that replaced it, which
 * the app does not do for the person (which checks it shows may have changed) - so Discard only,
 * and the sentence says to take it again on the corrected inspection.
 */
export function PhotoNotSent({
  photo,
  onRetry,
  onDiscard,
}: {
  photo: { context: { vendorName: string }; message: string; reason: string | null };
  onRetry: () => void;
  onDiscard: () => void;
}) {
  const superseded = photo.reason === 'inspection_superseded';
  return (
    <div role="alert" className={`${CARD} border-(--warn)`}>
      <p className="m-0 font-semibold">Photo not sent: {photo.context.vendorName}</p>
      <p className="m-0 text-(length:--fs-sm)">
        The inspection itself is saved.{' '}
        {superseded
          ? 'That inspection has since been corrected, so it takes no new photos. Attach the photo to the corrected inspection instead (ask QA), then discard it here.'
          : photo.message}
      </p>
      <div className="mt-(--s2) grid grid-cols-1 gap-(--s2) sm:flex sm:flex-wrap">
        {superseded ? null : (
          <button type="button" className={BTN} onClick={onRetry}>
            Retry photo
          </button>
        )}
        <button type="button" className={BTN} onClick={onDiscard}>
          Discard photo
        </button>
      </div>
    </div>
  );
}

/** What the queue keeps beside an inspection: IirContext plus the stage, so a result can say
 *  what a vehicle FAIL means for the goods. Stored as given; an older row has no stage. */
type ReceiveContext = IirContext & { stage?: 'VEHICLE' | 'GOODS' };
const stageOf = (c: IirContext): 'VEHICLE' | 'GOODS' | null => (c as ReceiveContext).stage ?? null;

/** The vehicle inspection, read-only, at the top of the goods form: who, when, carrier, rego, each check. */
export function VehicleSummary({ v }: { v: AwaitingVehicle }) {
  return (
    <section className={CARD} aria-label="Vehicle inspection">
      <h2 className="m-0 text-(length:--fs-lg) font-semibold wrap-anywhere">
        Vehicle inspection #{v.inspectionId}: {vehicleOutcome(v)}
      </h2>
      <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted) wrap-anywhere">
        {v.vendor.name ?? v.vendor.account ?? 'Unknown supplier'} · by {v.inspectedByUpn}
        {v.recordedAt ? ` · ${dockTime(v.recordedAt)}` : ''}
      </p>
      <dl className="m-0 mt-(--s2) grid grid-cols-1 gap-x-(--s4) gap-y-(--s1) sm:grid-cols-3">
        <div>
          <dt className={LABEL}>Date received</dt>
          <dd className="m-0">{dockDay(v.dateReceived)}</dd>
        </div>
        <div>
          <dt className={LABEL}>Delivery company</dt>
          <dd className="m-0 wrap-anywhere">{v.deliveryCompany ?? 'Not recorded'}</dd>
        </div>
        <div>
          <dt className={LABEL}>Vehicle registration</dt>
          <dd className="m-0 font-mono uppercase">{v.vehicleRego ?? 'Not recorded'}</dd>
        </div>
      </dl>
      <ul className="m-0 mt-(--s3) flex list-none flex-col gap-(--s1) p-0">
        {v.vehicle.map((c) => (
          <li key={c.check} className="flex flex-wrap gap-x-(--s2)">
            <span className={`font-semibold ${c.result === 'FAIL' ? 'text-(--bad)' : c.result === 'PASS' ? 'text-(--ok)' : 'text-(--muted)'}`}>
              {c.result === 'PASS' ? '✓ Pass' : c.result === 'FAIL' ? '✕ Fail' : 'Not asked'}
            </span>
            <span className="min-w-0 wrap-anywhere">
              {c.label}
              {c.reason ? ` - ${c.reason}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Receive() {
  useMsal();
  const upn = currentUpn();
  const queue = useIirQueue(upn);
  const grants = useMyGrants();
  const today = brisbaneToday();

  // Who sees what (contract, the receiving handoff): the vehicle inspection is RECEIVE's, the goods
  // inspection RELEASE's. A grants read that has not answered - failed or still loading - is NOT
  // "no grant" (rule 15): both paths are shown and the server decides.
  const canVehicle = !grants.known || grants.holds('RECEIVE');
  const canGoods = !grants.known || grants.holds('RELEASE');

  // The register's default window (14 days either side): today's deliveries are inside it. The goods
  // form reads it too, for the batches D365 received (a hint beside the lot).
  const register = useResource<RegisterPayload>('/receipts');
  // "Truck not listed?": every line still to come in, whatever its date (audit 2026-10-28) - a truck
  // days early or late is checked before D365 receives it. Read only when asked: a wider window.
  const [allOpen, setAllOpen] = useState(false);
  const openRegister = useResource<RegisterPayload>(allOpen ? openLinesQuery(today) : null);
  const pickerRows = useMemo(() => {
    if (!allOpen || !openRegister.data) return register.data?.rows ?? [];
    const seen = new Set((register.data?.rows ?? []).map((r) => r.receiptId));
    return [...(register.data?.rows ?? []), ...openRegister.data.rows.filter((r) => !seen.has(r.receiptId))];
  }, [allOpen, register.data, openRegister.data]);
  const groups = useMemo(() => deliveryGroups(pickerRows, today, allOpen), [pickerRows, today, allOpen]);
  const awaiting = useResource<{ inspections: AwaitingVehicle[] }>(canGoods ? '/receipt-inspections?awaiting=goods' : null);
  const toInspect = useMemo(() => (awaiting.data?.inspections ?? []).filter((v) => v.goodsInspectionId === null), [awaiting.data]);
  // QA's view before the warehouse (Amit, 29 Oct 2026): what D365 received with no dock inspection yet - the
  // receipt alert's own selection. Read-only for QA: the goods follow the vehicle check, never precede it.
  const dock = useResource<DockWaitingPayload>(canGoods ? '/dock-waiting' : null);

  const [group, setGroup] = useState<DeliveryGroup | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  // Which step the supplier picker is opening (2 Nov 2026: the goods inspection opens on its own too).
  const [pickFor, setPickFor] = useState<'VEHICLE' | 'GOODS'>('VEHICLE');
  const [draft, setDraft] = useState<Draft | null>(null);
  // The tab names the form on screen (UI/UX audit, 31 Oct 2026, UX-21): "Vehicle inspection · Receiving · Quality".
  usePageTitle(draft?.stage === 'VEHICLE' ? 'Vehicle inspection' : draft?.stage === 'GOODS' ? 'Goods inspection' : null);
  const [saved, setSaved] = useState<string | null>(null);
  // A submit attempt on a form with gaps (UI/UX audit, 31 Oct 2026, UX-44). A fresh form used to open
  // with up to six amber "is not answered" lines, none linked to its check - a wall of warnings for
  // checks nobody had had the chance to answer. Now it shows a neutral count until the person presses
  // the submit; only then the list, a marker on each gap and a jump to the first. The gate is
  // unchanged: problems() still names every gap and submit() still sends nothing while one remains.
  const [tried, setTried] = useState(false);
  const [jump, setJump] = useState(0);
  // The one in-page question (ConfirmPanel) in place of window.confirm, which
  // on a docked iPad kiosk is unstyled, shows the hostname and blocks the queue.
  const [confirmReq, setConfirmReq] = useState<ConfirmRequest | null>(null);

  // The half-done inspection on this tablet (UX-08). `draftOwner` is who OPENED the draft on
  // screen: it is kept under that name only, and if a different account is signed in meanwhile the
  // screen lets go of it rather than keep it under the newcomer's name - the owner's copy stays.
  const drafts = useIirDraft<Draft>(upn);
  const draftOwner = useRef('');
  const openDraft = useCallback(
    (d: Draft) => {
      draftOwner.current = upn;
      setTried(false);
      setDraft(d);
    },
    [upn],
  );
  // Starting a new inspection over a kept one would overwrite it silently (one per account):
  // asked first, naming the one that goes.
  const startDraft = (d: Draft) => {
    if (drafts.state.status !== 'found') return openDraft(d);
    const label = drafts.state.row.label;
    setConfirmReq({
      title: `Replace the unfinished inspection of ${label}?`,
      consequence: `What you entered for ${label}, photos included, is deleted from this tablet. It was never sent.`,
      confirmLabel: 'Replace it',
      keepLabel: 'Keep it',
      onConfirm: () => openDraft(d),
    });
  };
  const { keep: keepDraft } = drafts;
  useEffect(() => {
    if (!draft || !upn || draftOwner.current !== upn) return;
    keepDraft(upn, draft, draftSupplier(draft));
  }, [draft, upn, keepDraft]);
  useEffect(() => {
    if (draft && draftOwner.current !== upn) {
      setDraft(null);
      setGroup(null);
    }
  }, [upn, draft]);
  // A reload, a closed tab or a redirect asks first while an inspection is open. The draft is kept
  // either way (flushed on pagehide); this is for the write that could not land (saveFailed) and for
  // the person who did not mean to leave the truck half-checked.
  const drafting = draft !== null;
  useEffect(() => {
    if (!drafting) return;
    const ask = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older WebKit (the docked iPads) still reads returnValue rather than preventDefault.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [drafting]);

  // A "received, inspect now" Teams card links here with ?receipt=7,8 (rule 26): once the register
  // has loaded, open that supplier's lines with the linked ones ticked, for a vehicle inspection.
  // Once only - Back returns to the list as usual. A link to lines the register does not hold says
  // so (rule 15).
  const [params] = useSearchParams();
  const linkedIds = useMemo(() => linkedReceiptIds(params.get('receipt')), [params]);
  const [linkState, setLinkState] = useState<'pending' | 'done' | 'missing'>(linkedIds.length > 0 ? 'pending' : 'done');
  useEffect(() => {
    if (linkState !== 'pending' || !register.data) return;
    const found = linkedDelivery(register.data.rows, today, linkedIds);
    if (found) {
      // The card's "Inspect samples" button adds &step=goods (3 Nov 2026): the same lines, step 2.
      setPickFor(params.get('step') === 'goods' ? 'GOODS' : 'VEHICLE');
      setGroup(found.group);
      setPicked(new Set(found.picked));
      setLinkState('done');
    } else setLinkState('missing');
  }, [linkState, register.data, today, linkedIds, params]);

  // "Vehicle passed: inspect the goods" links here with ?vehicle=<id>: once QA's list has loaded,
  // open the goods form for it. Not on the list = not waiting (inspected, failed, corrected): said.
  const linkedVehicle = useMemo(() => linkedVehicleId(params.get('vehicle')), [params]);
  const [vehicleLink, setVehicleLink] = useState<'pending' | 'done' | 'missing'>(linkedVehicle !== null ? 'pending' : 'done');
  useEffect(() => {
    if (vehicleLink !== 'pending' || linkedVehicle === null || !awaiting.data) return;
    // The register is a hint for the batch rows only: wait for it unless it failed.
    if (!register.data && !register.error) return;
    // And for the kept draft (UX-08): opening the link before it is read would overwrite it unasked.
    if (drafts.state.status === 'loading') return;
    const v = toInspect.find((x) => x.inspectionId === linkedVehicle);
    if (v) {
      startDraft(newGoodsDraft(v, register.data?.rows ?? [], today));
      setVehicleLink('done');
    } else setVehicleLink('missing');
    // startDraft is a fresh closure each render; the 'pending' guard makes this act once.
  }, [vehicleLink, linkedVehicle, awaiting.data, toInspect, register.data, register.error, today, drafts.state.status]);

  const draftGaps = draft ? gaps(draft, today) : [];
  const blockers = draftGaps.map((g) => g.text);
  const flagged: ReadonlySet<string> = new Set(tried ? draftGaps.flatMap((g) => (g.target ? [g.target] : [])) : []);
  // The jump to the first gap (UX-44), after the render that shows the markers. A gap with nothing to
  // point at (no row at all) lands on the list itself. jsdom has no scrollIntoView: optional.
  const firstTarget = draftGaps.find((g) => g.target)?.target ?? 'iir-gaps';
  useEffect(() => {
    if (jump === 0) return;
    const el = document.getElementById(firstTarget);
    el?.scrollIntoView?.({ block: 'center' });
    el?.focus();
    // Once per press: firstTarget moves as gaps are answered, and must not pull focus each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump]);

  // Photos: functional updates, because a photo lands after its scaling, by which time the
  // draft may have moved on. ALWAYS OPTIONAL: problems() never looks at them.
  const addPhotos = useCallback(
    (rowKey: string | null, checkCode: PhotoCheckCode | null) => (ps: Array<Pick<DraftPhoto, 'key' | 'blob' | 'capturedAtDevice'>>) =>
      setDraft((d) => (d ? { ...d, photos: [...d.photos, ...ps.map((p) => ({ ...p, rowKey, checkCode }))] } : d)),
    [],
  );
  const removePhoto = useCallback((key: string) => setDraft((d) => (d ? { ...d, photos: d.photos.filter((p) => p.key !== key) } : d)), []);
  const photoSlot: PhotoSlot = {
    of: (rowKey, checkCode) => (draft?.photos ?? []).filter((p) => p.rowKey === rowKey && p.checkCode === checkCode),
    total: draft?.photos.length ?? 0,
    add: addPhotos,
    remove: removePhoto,
  };
  const fails = draft ? failedCount(draft) : 0;

  const choose = (g: DeliveryGroup, forStage: 'VEHICLE' | 'GOODS' = 'VEHICLE') => {
    setPickFor(forStage);
    setGroup(g);
    setSaved(null);
    // The vehicle check comes before the D365 receipt, so nothing is pre-ticked from D365: the
    // person ticks what is on the truck. One line only is ticked for them.
    setPicked(new Set(g.lines.length === 1 ? [g.lines[0].receiptId] : []));
  };

  async function submit() {
    if (!draft) return;
    // The gate (UX-44 changes how it is SHOWN, never what it refuses): a form with a gap sends nothing.
    if (blockers.length > 0) {
      setTried(true);
      setJump((n) => n + 1);
      return;
    }
    const photos = photosToSend(draft);
    const photoNote = photos.length > 0 ? `, ${photos.length} photo${photos.length === 1 ? '' : 's'}` : '';
    const sending = `It is sent as soon as there is a connection${photos.length > 0 ? '; the photos follow it' : ''}.`;
    if (draft.stage === 'VEHICLE') {
      const label = draft.lines.map((l) => `${poLine(l.po, l.line)} ${l.itemName}`).join(', ');
      const context: ReceiveContext = { vendorName: draft.vendorName, label: `Vehicle inspection: ${label}`, stage: 'VEHICLE' };
      await queue.submit(toPayload(draft), context, photos);
      setSaved(
        fails > 0
          ? `Saved on this tablet: vehicle from ${draft.vendorName}, ${fails} failed check${fails === 1 ? '' : 's'}${photoNote}. ` +
              `The goods stay on the truck until QA closes the failure. ${sending}`
          : `Saved on this tablet: vehicle from ${draft.vendorName} passed${photoNote}. Handed over to QA to inspect the goods. ${sending}`,
      );
    } else {
      const v = draft.vehicleInspection;
      const vendorName = draft.vendorName;
      const label = [...new Map(draft.rows.map((r) => [r.receiptId, `${poLine(r.po, r.line)} ${r.itemName}`])).values()].join(', ');
      const context: ReceiveContext = { vendorName, label: `Goods inspection${v ? ` after vehicle #${v.inspectionId}` : ''}: ${label}`, stage: 'GOODS' };
      await queue.submit(toPayload(draft), context, photos);
      setSaved(
        `Saved on this tablet: goods from ${vendorName}${fails > 0 ? `, ${fails} failed check${fails === 1 ? '' : 's'}` : ', all checks passed'}${photoNote}. ${sending}`,
      );
      awaiting.reload();
    }
    // In the queue now, so no longer a draft (UX-08): cleared AFTER the enqueue, so a submit that
    // fails to reach the queue leaves the kept copy where it was.
    void drafts.clear(upn).catch(() => undefined);
    setDraft(null);
    setGroup(null);
  }

  const leave = () =>
    setConfirmReq({
      title: 'Leave this inspection?',
      consequence: 'What you entered is not kept.',
      confirmLabel: 'Leave',
      keepLabel: 'Stay',
      onConfirm: () => {
        setDraft(null);
        // The owner cancelled: the kept copy goes too (UX-08). A clear that fails offers it again
        // next time - the safe way round.
        void drafts.clear(upn).catch(() => undefined);
      },
    });

  // The offer to pick a kept draft back up (UX-08). Only ever this account's (useIirDraft loads by
  // the signed-in UPN); another person's is not shown and is left for them.
  const keptRow = drafts.state.status === 'found' ? drafts.state.row : null;
  const kept = keptRow ? resumableDraft(keptRow.draft) : null;
  const discardKept = () =>
    setConfirmReq({
      title: `Discard the unfinished inspection of ${keptRow?.label ?? 'this supplier'}?`,
      consequence: 'What you entered, photos included, is deleted from this tablet. It was never sent.',
      confirmLabel: 'Discard inspection',
      keepLabel: 'Keep',
      onConfirm: () => void drafts.clear(upn).catch(() => undefined),
    });
  const resumeOffer = draft ? null : drafts.state.status === 'unreadable' ? (
    // Rule 15: a store that could not be read is not "nothing unfinished".
    <div role="alert" className={CARD}>
      <p className="m-0 font-semibold text-(--warn)">
        This tablet could not check for an unfinished inspection of yours. If you started one, it may still be here.
      </p>
      <button type="button" className={`${BTN} mt-(--s2)`} onClick={() => void drafts.reload()}>
        Try again
      </button>
    </div>
  ) : keptRow ? (
    <section className={`${CARD} border-(--warn)`} aria-label="Unfinished inspection">
      <h2 className="m-0 text-(length:--fs-lg) font-semibold wrap-anywhere">Unfinished inspection: {keptRow.label}</h2>
      <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
        {kept
          ? `Not sent. Kept on this tablet ${dockTime(keptRow.savedAt)}.`
          : 'Kept by an earlier version of the app, so it cannot be opened in this one. Discard it and start the inspection again.'}
      </p>
      <div className="mt-(--s3) grid grid-cols-1 gap-(--s3) sm:flex sm:flex-wrap">
        {kept ? (
          <button
            type="button"
            className={`${PRIMARY} wrap-anywhere`}
            onClick={() => {
              setSaved(null);
              setGroup(null);
              openDraft(kept);
            }}
          >
            Resume the inspection of {keptRow.label}
          </button>
        ) : null}
        <button type="button" className={BTN} onClick={discardKept}>
          Discard
        </button>
      </div>
    </section>
  ) : null;

  const queueBlock = (
    <div className="flex flex-col gap-(--s2)">
      {queue.pending === null ? (
        <p className="m-0 font-semibold text-(--warn)">
          This tablet cannot read its own inspection queue, so it cannot tell whether anything is still to send. Do
          not hand over; tell a supervisor.
        </p>
      ) : queue.pending > 0 ? (
        <p className="m-0 font-semibold">
          {queue.pending} inspection{queue.pending === 1 ? '' : 's'} waiting to send. They are stamped with your name.
        </p>
      ) : null}
      {queue.photosPending ? (
        <p className="m-0">
          {queue.photosPending} photo{queue.photosPending === 1 ? '' : 's'} waiting to send after {queue.photosPending === 1 ? 'its' : 'their'} inspection.
        </p>
      ) : null}
      {queue.photosHeld > 0 ? (
        <p className="m-0 text-(--warn)">
          {queue.photosHeld} photo{queue.photosHeld === 1 ? '' : 's'} taken by another account {queue.photosHeld === 1 ? 'is' : 'are'} held here until that person signs in.
        </p>
      ) : null}
      {queue.photosFailed.map((p) => (
        <PhotoNotSent
          key={p.photoId}
          photo={p}
          onRetry={() => void queue.retryPhoto(p.photoId, upn)}
          onDiscard={() =>
            setConfirmReq({
              title: 'Discard this photo?',
              consequence: 'The inspection is kept; only the photo goes.',
              confirmLabel: 'Discard photo',
              keepLabel: 'Keep',
              onConfirm: () => void queue.discardPhoto(p.photoId, upn),
            })
          }
        />
      ))}
      {queue.held.length > 0 ? (
        <p className="m-0 text-(--warn)">
          {queue.held.length} inspection{queue.held.length === 1 ? '' : 's'} recorded by{' '}
          {[...new Set(queue.held.map((h) => h.upn || 'an unknown account'))].join(', ')} are held here until that
          person signs in on this tablet.
        </p>
      ) : null}
      {queue.refused.map((r) => {
        const hint = refusalHint(r.reason);
        return (
          <div key={r.queueId} role="alert" className={`${CARD} border-(--bad)`}>
            <p className="m-0 font-semibold">Not accepted: {r.context.vendorName}</p>
            <p className="m-0 text-(length:--fs-sm)">{r.context.label}</p>
            <p className="m-0 mt-(--s1)">{r.message}</p>
            {hint ? <p className="m-0 mt-(--s1) font-semibold">{hint}</p> : null}
            <div className="mt-(--s2) grid grid-cols-1 gap-(--s2) sm:flex sm:flex-wrap">
              <button type="button" className={BTN} onClick={() => void queue.retry(r.queueId, upn)}>
                Send again
              </button>
              <button
                type="button"
                className={BTN}
                onClick={() =>
                  setConfirmReq({
                    title: 'Discard this inspection from the tablet?',
                    consequence: 'It was never accepted by the server.',
                    confirmLabel: 'Discard inspection',
                    keepLabel: 'Keep',
                    onConfirm: () => void queue.discard(r.queueId, upn),
                  })
                }
              >
                Discard (re-enter it instead)
              </button>
            </div>
          </div>
        );
      })}
      {queue.results.length > 0 ? (
        <div className={CARD} aria-live="polite">
          {queue.results.map((r) => {
            const stage = stageOf(r.context);
            const what = stage === 'VEHICLE' ? 'vehicle' : stage === 'GOODS' ? 'goods' : null;
            return (
              <p key={r.clientInspectionId} className="m-0">
                Sent: {what ? `${what} from ` : ''}
                {r.context.vendorName} - {r.result === 'FAIL' ? 'FAIL' : r.result === 'PASS' ? 'all passed' : 'recorded'}.
                {r.failureLine ? ` ${r.failureLine}` : ''}
                {stage === 'VEHICLE' && r.result === 'FAIL' ? ' The goods stay on the truck until QA closes the failure.' : ''}
                {stage === 'VEHICLE' && r.result === 'PASS' ? ' QA has it in Goods to inspect.' : ''}
              </p>
            );
          })}
          <button type="button" className={`${BTN} mt-(--s2)`} onClick={queue.acknowledgeResults}>
            OK
          </button>
        </div>
      ) : null}
    </div>
  );

  // No "Line check" button here: the shell's area switcher is the one way between areas (27 Oct 2026).
  const header = (
    <div className="flex flex-wrap items-center gap-(--s3)">
      <h1 className="m-0 min-w-0 text-(length:--fs-xl) font-bold">Receiving</h1>
    </div>
  );

  const grantNote = grants.known ? (
    !grants.holds('RECEIVE') && !grants.holds('RELEASE') ? (
      <p className="m-0 font-semibold text-(--warn)">
        {/* Plain words first, codes after, no Office menu path: a dock user has no Office (UI/UX
            audit, 31 Oct 2026, UX-17). */}
        You are not set up to record dock inspections yet, so the server will not accept one from you. Ask a QA
        manager to grant {actionWithCode('RECEIVE')} for the vehicle inspection, or {actionWithCode('RELEASE')} for
        the goods inspection.
      </p>
    ) : null
  ) : grants.error !== null ? (
    <div role="alert" className={CARD}>
      <p className="m-0 font-semibold text-(--warn)">
        Your authorisations could not be read, so both the vehicle and the goods inspection are shown. The server
        decides whether it takes yours.
      </p>
      <button type="button" className={`${BTN} mt-(--s2)`} onClick={grants.reload}>
        Try again
      </button>
    </div>
  ) : null;

  // Before a submit attempt: a neutral count. After one: every gap, in the form's order, with a jump
  // to the first (UX-44). role="alert" so the list is announced when the press reveals it.
  const progress = draft ? answeredCount(draft) : null;
  const blockerList = progress ? (
    <div className="flex flex-col gap-(--s2)">
      <p className="m-0 text-(length:--fs-sm) text-(--muted)">
        {progress.answered} of {progress.total} checks answered.
      </p>
      {tried && blockers.length > 0 ? (
        <div id="iir-gaps" tabIndex={-1} role="alert" className="flex flex-col gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">
            Not sent yet: {blockers.length} thing{blockers.length === 1 ? '' : 's'} to finish first.
          </p>
          <ul className="m-0 flex list-none flex-col gap-(--s1) p-0 text-(--warn)">
            {blockers.slice(0, 6).map((b) => (
              <li key={b}>{b}</li>
            ))}
            {blockers.length > 6 ? <li>…and {blockers.length - 6} more.</li> : null}
          </ul>
          <button type="button" className={`${BTN} w-full sm:w-auto sm:self-start`} onClick={() => setJump((n) => n + 1)}>
            Go to the first one
          </button>
        </div>
      ) : null}
    </div>
  ) : null;

  // A way out at the TOP of the form too (UI/UX audit, 31 Oct 2026, UX-53): on a phone the form is
  // long, and its only exit was Cancel below the last check. Same action, same confirmation.
  const topCancel = (
    <div className="flex justify-end">
      <button type="button" className={`${BTN} w-full sm:w-auto`} onClick={leave}>
        Cancel
      </button>
    </div>
  );

  const commentsBox = (value: string, onChange: (s: string) => void) => (
    <section className={CARD}>
      <Field label="Non-conformance details / comments">
        <textarea className={`${INPUT} min-h-[112px] py-(--s2)`} value={value} onChange={(e) => onChange(e.target.value)} />
      </Field>
    </section>
  );

  let body: React.ReactNode;
  if (draft?.stage === 'VEHICLE') {
    body = (
      <div className="flex flex-col gap-(--s4)">
        {topCancel}
        <section className={CARD} aria-label="Delivery">
          <h2 className="m-0 text-(length:--fs-lg) font-semibold">Vehicle inspection: {draft.vendorName}</h2>
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted) wrap-anywhere">
            On the truck: {draft.lines.map((l) => `${l.itemName} (${poLine(l.po, l.line)})`).join(' · ')}
          </p>
          <div className="mt-(--s3) flex flex-wrap gap-(--s3)">
            <Field label="Date received">
              <input id={fieldId('dateReceived')} type="date" className={INPUT} value={draft.dateReceived} onChange={(e) => setDraft({ ...draft, dateReceived: e.target.value })} />
            </Field>
            <Field label="Delivery company">
              <input className={INPUT} value={draft.deliveryCompany} onChange={(e) => setDraft({ ...draft, deliveryCompany: e.target.value })} />
            </Field>
            <Field label="Vehicle registration">
              <input className={`${INPUT} font-mono uppercase`} value={draft.vehicleRego} onChange={(e) => setDraft({ ...draft, vehicleRego: e.target.value })} />
            </Field>
          </div>
          <p className="m-0 mt-(--s2) text-(length:--fs-sm) text-(--muted) wrap-anywhere">Inspected by {upn || 'nobody signed in'}.</p>
          <div className="mt-(--s3) flex flex-col gap-(--s1)">
            <span className={LABEL}>Photos of the vehicle (optional)</span>
            <PhotoPicker label="the vehicle" photos={photoSlot.of(null, null)} total={photoSlot.total} onAdd={addPhotos(null, null)} onRemove={removePhoto} />
          </div>
        </section>

        <section className={CARD} aria-label="Vehicle checks">
          <h2 className="m-0 text-(length:--fs-lg) font-semibold">Vehicle</h2>
          {VEHICLE_CHECKS.map((c) => (
            <CheckToggle
              key={c.key}
              label={c.label}
              value={draft.vehicle[c.key]}
              onChange={(a) => setDraft({ ...draft, vehicle: { ...draft.vehicle, [c.key]: a } })}
              photo={{ slot: photoSlot, rowKey: null, code: c.key, where: 'vehicle' }}
              id={checkId(null, c.key)}
              flagged={flagged.has(checkId(null, c.key))}
            />
          ))}
        </section>

        {commentsBox(draft.comments, (comments) => setDraft({ ...draft, comments }))}
        {blockerList}
        <div className="grid grid-cols-1 gap-(--s3) pb-(--s5) sm:flex sm:flex-wrap">
          {/* "Record vehicle check", not "Hand over to QA" (UI/UX audit, 31 Oct 2026, UX-53): "Hand over"
              is the shell's sign-out for a shared tablet, and the two read as one. Pressable while gaps
              remain (aria-disabled, UX-44) so the press can show them; submit() still sends nothing. */}
          <button type="button" className={SUBMIT} aria-disabled={blockers.length > 0} disabled={!upn} onClick={() => void submit()}>
            {fails > 0 ? `Record vehicle check (${fails} failed)` : 'Record vehicle check'}
          </button>
          <button type="button" className={BTN} onClick={leave}>
            Cancel
          </button>
        </div>
      </div>
    );
  } else if (draft?.stage === 'GOODS') {
    const v = draft.vehicleInspection;
    const truck = draft.truck;
    body = (
      <div className="flex flex-col gap-(--s4)">
        {topCancel}
        {v ? (
          <VehicleSummary v={v} />
        ) : truck ? (
          // On its own (2 Nov 2026): the truck as QA saw it. Only the date is required.
          <section className={CARD} aria-label="Delivery">
            <h2 className="m-0 text-(length:--fs-lg) font-semibold wrap-anywhere">Goods inspection: {draft.vendorName}</h2>
            <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
              Recorded without a vehicle inspection. The warehouse can still record the vehicle check separately.
            </p>
            <div className="mt-(--s3) grid grid-cols-1 gap-(--s3) sm:grid-cols-3">
              <Field label="Date received">
                <input
                  id={fieldId('dateReceived')}
                  type="date"
                  className={INPUT}
                  value={truck.dateReceived}
                  max={today}
                  onChange={(e) => setDraft({ ...draft, truck: { ...truck, dateReceived: e.target.value } })}
                />
              </Field>
              <Field label="Delivery company (if known)">
                <input className={INPUT} value={truck.deliveryCompany} onChange={(e) => setDraft({ ...draft, truck: { ...truck, deliveryCompany: e.target.value } })} />
              </Field>
              <Field label="Vehicle registration (if known)">
                <input className={`${INPUT} font-mono uppercase`} value={truck.vehicleRego} onChange={(e) => setDraft({ ...draft, truck: { ...truck, vehicleRego: e.target.value } })} />
              </Field>
            </div>
          </section>
        ) : null}
        <p className="m-0 text-(length:--fs-sm) text-(--muted) wrap-anywhere">Goods inspected by {upn || 'nobody signed in'}.</p>
        {draft.rows.map((row, i) => (
          <BatchCard
            key={row.key}
            row={row}
            index={i}
            onChange={(r) => setDraft({ ...draft, rows: draft.rows.map((x) => (x.key === r.key ? r : x)) })}
            // The last row of a PO line leaves the LINE out - the warehouse ticked it, it is not on
            // the truck - with a reason, never attested to (audit 2026-10-28).
            onRemove={
              draft.rows.filter((x) => x.receiptId === row.receiptId).length > 1
                ? () => setDraft({ ...draft, rows: draft.rows.filter((x) => x.key !== row.key), photos: draft.photos.filter((p) => p.rowKey !== row.key) })
                : () => setDraft(leaveOutLine(draft, row.receiptId))
            }
            removeLabel={draft.rows.filter((x) => x.receiptId === row.receiptId).length > 1 ? 'Remove row' : v ? 'Not on the truck' : 'Leave this line out'}
            photos={photoSlot}
            flagged={flagged}
          />
        ))}
        {draft.leftOut.map((l) => (
          <section key={l.receiptId} className={CARD} aria-label={`Left out: ${poLine(l.po, l.line)}`}>
            <h3 className="m-0 text-(length:--fs-lg) font-semibold wrap-anywhere">
              Left out: {l.itemName} ({poLine(l.po, l.line)})
            </h3>
            <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
              Not inspected. The reason goes on the record&apos;s comments.
            </p>
            <div className="mt-(--s3) flex flex-col gap-(--s3) sm:flex-row sm:items-end">
              <Field label="Why is it left out?">
                <input
                  id={fieldId('leftOut', l.receiptId)}
                  className={INPUT}
                  value={l.reason}
                  placeholder="Not on the truck"
                  onChange={(e) =>
                    setDraft({ ...draft, leftOut: draft.leftOut.map((x) => (x.receiptId === l.receiptId ? { ...x, reason: e.target.value } : x)) })
                  }
                />
              </Field>
              <button type="button" className={BTN} onClick={() => setDraft(restoreLine(draft, l.receiptId))}>
                Put it back
              </button>
            </div>
          </section>
        ))}
        <div className="grid grid-cols-1 gap-(--s2) sm:flex sm:flex-wrap">
          {draft.lines
            .filter((l) => !draft.leftOut.some((x) => x.receiptId === l.receiptId))
            .map((l) => (
              <button
                key={l.receiptId}
                type="button"
                className={`${BTN} text-left wrap-anywhere`}
                onClick={() => setDraft({ ...draft, rows: [...draft.rows, blankRow(lineAsRegister(l))] })}
              >
                + Another batch of {l.itemName ?? l.item} ({poLine(l.po, l.line)})
              </button>
            ))}
        </div>
        {commentsBox(draft.comments, (comments) => setDraft({ ...draft, comments }))}
        {blockerList}
        <div className="grid grid-cols-1 gap-(--s3) pb-(--s5) sm:flex sm:flex-wrap">
          <button type="button" className={SUBMIT} aria-disabled={blockers.length > 0} disabled={!upn} onClick={() => void submit()}>
            {fails > 0 ? `Submit goods inspection (${fails} failed)` : 'Submit goods inspection'}
          </button>
          <button type="button" className={BTN} onClick={leave}>
            Cancel
          </button>
        </div>
      </div>
    );
  } else if (group) {
    body = (
      <section className={CARD} aria-label="PO lines on this truck">
        <h2 className="m-0 text-(length:--fs-lg) font-semibold wrap-anywhere">
          {group.vendorName}: which lines {pickFor === 'GOODS' ? 'are you inspecting' : 'are on this truck'}?
        </h2>
        <div className="mt-(--s3) flex flex-col gap-(--s2)">
          {/* Each line's dock state, not-inspected first (UI/UX audit, 31 Oct 2026, UX-43): the picker
              read only the due date and D365 batches, so a line the warehouse had already checked looked
              the same as one nobody had. The register's own word; nothing is disabled - a second truck
              on a line already checked is legitimate. */}
          {pickerLines(group.lines).map((l) => {
            const state = lineInspection(l);
            return (
            <label key={l.receiptId} className={`${BTN} flex cursor-pointer items-center gap-(--s3) py-(--s2)`}>
              <input
                type="checkbox"
                className="h-[28px] w-[28px] flex-none"
                checked={picked.has(l.receiptId)}
                onChange={(e) => {
                  const next = new Set(picked);
                  if (e.target.checked) next.add(l.receiptId);
                  else next.delete(l.receiptId);
                  setPicked(next);
                }}
              />
              <span className="min-w-0 flex-1 text-left wrap-anywhere">
                {l.itemName ?? l.item}
                <span className="block font-mono text-(length:--fs-sm) font-normal text-(--muted)">
                  {poLine(l.po, l.line)} · due {l.expectedDate ? dockDay(l.expectedDate) : 'not known'}
                  {l.batches.length > 0 ? ` · D365 received: ${l.batches.map((b) => b.batchId).join(', ')}` : ''}
                </span>
                <span className={`block text-(length:--fs-sm) font-semibold ${TONE_TEXT[state.tone]}`}>{state.text}</span>
              </span>
            </label>
            );
          })}
        </div>
        <div className="mt-(--s4) grid grid-cols-1 gap-(--s3) sm:flex sm:flex-wrap">
          <button
            type="button"
            className={PRIMARY}
            disabled={picked.size === 0}
            onClick={() => {
              const lines = group.lines.filter((l) => picked.has(l.receiptId));
              startDraft(pickFor === 'GOODS' ? newGoodsDraftApart(group, lines, today) : newVehicleDraft(group, lines, today));
            }}
          >
            {pickFor === 'GOODS' ? 'Start goods inspection' : 'Start vehicle inspection'}
          </button>
          <button type="button" className={BTN} onClick={() => setGroup(null)}>
            Back
          </button>
        </div>
      </section>
    );
  } else {
    const goodsList = canGoods ? (
      <section className={`${CARD} flex flex-col gap-(--s3) border-l-4 border-l-(--primary)`} aria-label="Goods to inspect">
        <StepHeading step={2} title="Goods (sample) inspection" who="QA. Pick a truck the warehouse has checked, or start one on its own." />
        {vehicleLink === 'missing' ? (
          <p className="m-0 font-semibold text-(--warn)">
            Vehicle inspection #{linkedVehicle} is not waiting for a goods inspection: its goods may already be inspected,
            the vehicle failed, or it was corrected. Pick from the list below.
          </p>
        ) : null}
        {awaiting.error ? (
          <div role="alert" className={CARD}>
            <p className="m-0 font-semibold text-(--warn)">
              Could not read the trucks waiting for QA: {awaiting.error}. This is not the same as "nothing to inspect".
            </p>
            <button type="button" className={`${BTN} mt-(--s2)`} onClick={awaiting.reload}>
              Try again
            </button>
          </div>
        ) : !awaiting.data ? (
          <p role="status" className="m-0 text-(--muted)">Loading the trucks waiting for QA…</p>
        ) : toInspect.length === 0 ? (
          <p className="m-0 text-(--muted)">No vehicle inspection is waiting for its goods to be inspected.</p>
        ) : (
          <div className="grid grid-cols-1 gap-(--s3) md:grid-cols-2">
            {toInspect.map((v) => (
              <button
                key={v.inspectionId}
                type="button"
                onClick={() => {
                  setSaved(null);
                  startDraft(newGoodsDraft(v, register.data?.rows ?? [], today));
                }}
                className={`${CARD} min-h-[96px] text-left active:bg-(--primary-soft) ${opFocus}`}
              >
                <span className="block text-(length:--fs-lg) font-semibold wrap-anywhere">{v.vendor.name ?? v.vendor.account ?? 'Unknown supplier'}</span>
                <span className="block text-(length:--fs-sm) text-(--muted) wrap-anywhere">
                  Vehicle #{v.inspectionId} {vehicleOutcome(v).toLowerCase()} · {v.dateReceived ? dockDay(v.dateReceived) : 'date not known'} · by {v.inspectedByUpn}
                </span>
                <span className="block text-(length:--fs-sm) text-(--muted) wrap-anywhere">
                  {v.lines.map((l) => `${l.itemName ?? l.item} (${poLine(l.po, l.line)})`).join(' · ')}
                </span>
              </button>
            ))}
          </div>
        )}
        {/* On its own (Amit, 2 Nov 2026): the goods inspection does not wait for the vehicle check. The
            same supplier picker as step 1; the server gates it on nothing. */}
        {register.data && groups.length > 0 ? (
          <div className="flex flex-col gap-(--s2)" aria-label="Start a goods inspection on its own">
            <p className="m-0 text-(length:--fs-sm) text-(--muted)">Or start a goods inspection without waiting for the vehicle check:</p>
            <div className="grid grid-cols-1 gap-(--s3) md:grid-cols-2">
              {groups.map((g) => (
                <button key={`g-${g.vendorKey}`} type="button" onClick={() => choose(g, 'GOODS')} className={`${CARD} min-h-[72px] text-left active:bg-(--primary-soft) ${opFocus}`}>
                  <span className="block font-semibold wrap-anywhere">{g.vendorName}</span>
                  <span className="block text-(length:--fs-sm) text-(--muted) wrap-anywhere">
                    {g.lines.map((l) => `${l.itemName ?? l.item} (${poLine(l.po, l.line)})`).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>
    ) : linkedVehicle !== null ? (
      <p className="m-0 font-semibold text-(--warn)">
        This link opens a goods inspection, which is QA's to record (the RELEASE authorisation). Pass it to QA.
      </p>
    ) : null;

    const dockList = canGoods ? <DockWaitingList dock={dock} /> : null;

    const vehicleStart = canVehicle ? (
      <section className={`${CARD} flex flex-col gap-(--s3) border-l-4 border-l-(--primary)`} aria-label="Start a vehicle inspection">
        <StepHeading step={1} title="Vehicle (transport) inspection" who="The warehouse, at the truck before unloading." />
        {linkState === 'missing' ? (
          <p className="m-0 font-semibold text-(--warn)">
            The PO line{linkedIds.length === 1 ? '' : 's'} this link names {linkedIds.length === 1 ? 'is' : 'are'} not in the goods-in
            register&apos;s window. Pick the supplier below, or ask QA to refresh the register from D365.
          </p>
        ) : null}
        {register.error ? (
          <div role="alert" className={CARD}>
            <p className="m-0 font-semibold text-(--warn)">
              Could not read today&apos;s deliveries: {register.error}. This is not the same as "nothing is due".
            </p>
            <button type="button" className={`${BTN} mt-(--s2)`} onClick={register.reload}>
              Try again
            </button>
          </div>
        ) : !register.data ? (
          <p role="status" className="m-0 text-(--muted)">Loading today&apos;s deliveries…</p>
        ) : groups.length === 0 ? (
          <p className="m-0 text-(--muted)">
            {allOpen
              ? 'No ingredient PO line is still to come in, a month either side of today, in the goods-in register'
              : 'No ingredient deliveries are due or received within three days of today in the goods-in register'}
            {register.data.lastSyncedAt ? ` (refreshed from D365 ${dockTime(register.data.lastSyncedAt)})` : ''}.
            If a truck is here, QA can refresh the register from D365.
          </p>
        ) : (
          <>
            <p className="m-0 text-(--muted)">
              {allOpen ? 'Every PO line still to come in, whatever its date. ' : ''}Which supplier&apos;s truck is at the dock?
            </p>
            <div className="grid grid-cols-1 gap-(--s3) md:grid-cols-2">
              {groups.map((g) => (
                <button key={g.vendorKey} type="button" onClick={() => choose(g)} className={`${CARD} min-h-[96px] text-left active:bg-(--primary-soft) ${opFocus}`}>
                  <span className="block text-(length:--fs-lg) font-semibold wrap-anywhere">{g.vendorName}</span>
                  <span className="block text-(length:--fs-sm) text-(--muted) wrap-anywhere">
                    {g.lines.map((l) => `${l.itemName ?? l.item} (${poLine(l.po, l.line)})`).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
        {register.data ? (
          allOpen ? (
            openRegister.error ? (
              <div role="alert" className={CARD}>
                <p className="m-0 font-semibold text-(--warn)">
                  Could not read the open PO lines: {openRegister.error}. Only today&apos;s deliveries are shown.
                </p>
                <button type="button" className={`${BTN} mt-(--s2)`} onClick={openRegister.reload}>
                  Try again
                </button>
              </div>
            ) : !openRegister.data ? (
              <p role="status" className="m-0 text-(--muted)">Loading every open PO line…</p>
            ) : (
              <button type="button" className={BTN} onClick={() => setAllOpen(false)}>
                Show only today&apos;s deliveries
              </button>
            )
          ) : (
            <button type="button" className={BTN} onClick={() => setAllOpen(true)}>
              Truck not listed? Show every open PO line
            </button>
          )
        ) : null}
      </section>
    ) : null;

    // Two steps, two cards, in the order they happen (QA team feedback, 2 Nov 2026: "separate the
    // transport inspection and the samples inspection as distinct steps with distinct cards"). What
    // D365 received with no dock inspection yet belongs to step 1, so QA sees it there.
    body = (
      <div className="flex flex-col gap-(--s5)">
        {vehicleStart}
        {dockList}
        {goodsList}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-(--s4) overflow-y-auto p-(--s4)">
      {header}
      {/* "Saved on this tablet" is announced (UI/UX audit, 31 Oct 2026, UX-46): a status region that
          is always in the page - one mounted with its text already in it is often not read out - and
          shown, as before, only on the list once the form has closed. */}
      <div role="status" className="font-semibold">
        {saved && !draft && !group ? <p className="m-0">{saved}</p> : null}
      </div>
      {grantNote}
      {queueBlock}
      {resumeOffer}
      {draft && drafts.saveFailed ? (
        <p role="alert" className="m-0 font-semibold text-(--warn)">
          This tablet could not keep this inspection as you go. Stay on this screen until it is handed over or
          submitted: leaving it now loses what you entered.
        </p>
      ) : null}
      {body}
      {confirmReq ? <ConfirmPanel request={confirmReq} onClose={() => setConfirmReq(null)} /> : null}
    </div>
  );
}

export default Receive;
